import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAiChatStore } from "../../../store/aiChatStore";
import type { ChatMessage } from "../../../store/aiChatStore";
import { useTheme } from "../../../context/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faPaperPlane,
  faTrash,
  faGear,
  faUser,
  faSpinner,
  faTimes,
  faKey,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

function MessageBubble({ message, isDarkMode }: { message: ChatMessage; isDarkMode: boolean }) {
  const isUser = message.role === "user";

  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""} mb-4`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser
            ? isDarkMode ? "bg-blue-600" : "bg-blue-500"
            : "bg-gradient-to-br from-emerald-500 to-cyan-500"
        }`}
      >
        <FontAwesomeIcon
          icon={isUser ? faUser : faRobot}
          className="text-white text-sm"
        />
      </div>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
          isUser
            ? isDarkMode
              ? "bg-blue-600 text-white"
              : "bg-blue-500 text-white"
            : isDarkMode
              ? "bg-dark-surface text-gray-200"
              : "bg-gray-100 text-gray-800"
        }`}
      >
        {message.isLoading ? (
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-sm" />
            <span className="text-sm opacity-70">Thinking...</span>
          </div>
        ) : (
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</div>
        )}
      </div>
    </div>
  );
}

function SettingsPanel({
  isDarkMode,
  isOpen,
  onClose,
}: {
  isDarkMode: boolean;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { apiKey, model, setApiKey, setModel } = useAiChatStore();
  const [tempKey, setTempKey] = useState(apiKey);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setApiKey(tempKey);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`absolute top-0 right-0 w-full sm:w-80 h-full z-10 border-l ${
        isDarkMode ? "bg-dark-bg border-dark-surface" : "bg-white border-gray-200"
      }`}
    >
      <div className="p-4 flex flex-col h-full">
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Settings
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode ? "hover:bg-dark-surface text-gray-400" : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="space-y-4 flex-1">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
              <FontAwesomeIcon icon={faKey} className="mr-2" />
              OpenAI API Key
            </label>
            <input
              type="password"
              value={tempKey}
              onChange={(e) => setTempKey(e.target.value)}
              placeholder="sk-..."
              className={`w-full px-3 py-2 rounded-lg text-sm border ${
                isDarkMode
                  ? "bg-dark-surface border-dark-card text-white placeholder-gray-500"
                  : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400"
              } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
              Model
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg text-sm border ${
                isDarkMode
                  ? "bg-dark-surface border-dark-card text-white"
                  : "bg-gray-50 border-gray-300 text-gray-900"
              } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
            >
              <option value="gpt-4o-mini">GPT-4o Mini (Fast & Cheap)</option>
              <option value="gpt-4o">GPT-4o (Best Quality)</option>
              <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
              <option value="gpt-4.1">GPT-4.1</option>
            </select>
          </div>

          <button
            onClick={handleSave}
            className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              saved
                ? "bg-emerald-500 text-white"
                : "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:opacity-90"
            }`}
          >
            {saved ? (
              <span><FontAwesomeIcon icon={faCheck} className="mr-2" />Saved!</span>
            ) : (
              "Save Settings"
            )}
          </button>
        </div>

        <div className={`text-xs mt-4 p-3 rounded-lg ${isDarkMode ? "bg-dark-surface text-gray-400" : "bg-gray-50 text-gray-500"}`}>
          Your API key is stored locally in your browser and never sent to our servers.
        </div>
      </div>
    </div>
  );
}

export default function AiChat() {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation("common");
  const { messages, isLoading, apiKey, sendMessage, clearMessages } = useAiChatStore();
  const [input, setInput] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput("");
    sendMessage(trimmed);
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  };

  return (
    <div className={`flex flex-col h-[calc(100vh-64px)] relative ${isDarkMode ? "bg-dark-bg" : "bg-white"}`}>
      {/* Top bar */}
      <div
        className={`flex items-center justify-between px-4 py-3 border-b ${
          isDarkMode ? "border-dark-surface" : "border-gray-200"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
            <FontAwesomeIcon icon={faRobot} className="text-white" />
          </div>
          <div>
            <h2 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {t("apps.ai.name")}
            </h2>
            <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("apps.ai.description")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button
              onClick={clearMessages}
              className={`p-2 rounded-lg text-sm transition-colors ${
                isDarkMode ? "hover:bg-dark-surface text-gray-400" : "hover:bg-gray-100 text-gray-500"
              }`}
              title="Clear chat"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-lg text-sm transition-colors ${
              showSettings
                ? "bg-emerald-500/20 text-emerald-400"
                : isDarkMode
                  ? "hover:bg-dark-surface text-gray-400"
                  : "hover:bg-gray-100 text-gray-500"
            }`}
            title="Settings"
          >
            <FontAwesomeIcon icon={faGear} />
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faRobot} className="text-white text-2xl" />
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {t("apps.ai.name")}
            </h3>
            <p className={`text-sm mb-6 max-w-md ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("apps.ai.description")}
            </p>
            {!apiKey && (
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:opacity-90 transition-opacity"
              >
                <FontAwesomeIcon icon={faKey} className="mr-2" />
                Configure API Key
              </button>
            )}
            {apiKey && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                {[
                  "How many vehicles are in my fleet?",
                  "Show me recent alerts",
                  "Which devices are offline?",
                  "Summarize today's trips",
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => sendMessage(suggestion)}
                    className={`text-left p-3 rounded-xl text-sm transition-colors border ${
                      isDarkMode
                        ? "border-dark-surface hover:bg-dark-surface text-gray-300"
                        : "border-gray-200 hover:bg-gray-50 text-gray-600"
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} isDarkMode={isDarkMode} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input area */}
      <div
        className={`p-4 border-t ${
          isDarkMode ? "border-dark-surface" : "border-gray-200"
        }`}
      >
        <div
          className={`flex items-end gap-2 rounded-2xl border px-4 py-2 ${
            isDarkMode
              ? "bg-dark-surface border-dark-card"
              : "bg-gray-50 border-gray-300"
          } focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-transparent transition-all`}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={apiKey ? "Ask something about your fleet..." : "Configure your API key first..."}
            disabled={!apiKey}
            rows={1}
            className={`flex-1 resize-none bg-transparent text-sm py-1 focus:outline-none ${
              isDarkMode
                ? "text-white placeholder-gray-500"
                : "text-gray-900 placeholder-gray-400"
            } disabled:opacity-50`}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading || !apiKey}
            className={`p-2 rounded-xl transition-all flex-shrink-0 ${
              input.trim() && !isLoading && apiKey
                ? "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:opacity-90"
                : isDarkMode
                  ? "text-gray-600"
                  : "text-gray-300"
            }`}
          >
            <FontAwesomeIcon icon={isLoading ? faSpinner : faPaperPlane} className={isLoading ? "animate-spin" : ""} />
          </button>
        </div>
        <p className={`text-xs mt-2 text-center ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}>
          Powered by OpenAI &middot; AI Assistant POC
        </p>
      </div>

      {/* Settings panel overlay */}
      <SettingsPanel
        isDarkMode={isDarkMode}
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
}
